import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { withData } from 'util/hoc';
import { resolve } from 'library/helpers/path-resolver';
import { Random } from 'library/helpers/random';
import { Header5 } from 'library/components/text-elements/headings';
import { BodyText } from 'library/components/text-elements/text';
import Input from 'library/components/form-components/input';
import RoiQuestions from 'apps/roi/graph-calls/queries/roi-questions';
import * as StyledRoiChart from 'apps/roi/components/charts/styled-roi-chart';
import {
  CheckBoxWrapper,
  StyledColumn,
  StyledOption,
  StyledRow,
  StyledSurveyButton,
  StyledSurveyOptionsWrapper,
} from 'apps/roi/pages/roi-survey/styled-roi-survey';
import { CheckboxStatus } from 'apps/roi/pages/roi-survey/components/checkbox-status';
import { SearchPrograms } from 'apps/roi/pages/roi-survey/components/search-programs';

const SurveyQuestion = ({
  data,
  answers,
  setAnswers,
  programMap,
  setProgramMap,
  loading,
  setLoading,
}) => {
  const questionData = [...data.roi_questions] || [];

  const mainQuestion = questionData?.splice(0, 1)[0];
  const subQuestions = questionData;

  const mainQuestionOptions =
    mainQuestion?.answer_data?.length > 0
      ? JSON.parse(mainQuestion?.answer_data).answer_choices
      : [];

  let isActive;
  if (mainQuestion.answer_input_field_type === 'boolean') {
    isActive = answers[mainQuestion.key_to_update] === false;
    if (answers[mainQuestion.key_to_update]) {
      isActive = !subQuestions.some((subQuestion) =>
        isEmpty(answers[subQuestion.key_to_update])
      );
    }
  } else {
    isActive = !isEmpty(answers[mainQuestion.key_to_update]);
  }

  const hasSubQuestions = subQuestions.length > 0;

  const setAnswer = (keyName, value) => {
    setAnswers({ ...answers, [keyName]: value });
  };

  return mainQuestion?.answer_input_field_type === 'search-integer' ? (
    <SearchPrograms
      mainQuestion={mainQuestion}
      answers={answers}
      setAnswer={setAnswer}
      programMap={programMap}
      setProgramMap={setProgramMap}
      loading={loading}
      setLoading={setLoading}
    />
  ) : (
    <StyledRow>
      <CheckBoxWrapper>
        <CheckboxStatus active={isActive} />
      </CheckBoxWrapper>
      <StyledColumn>
        <StyledRow>
          <Header5>{mainQuestion?.text}</Header5>
        </StyledRow>
        <StyledSurveyOptionsWrapper>
          {mainQuestionOptions.map((answerOption) => (
            <StyledOption key={`${mainQuestion.id}${answerOption.ui_name}`}>
              <StyledSurveyButton
                active={
                  answerOption.value === answers[mainQuestion.key_to_update]
                }
                onClick={() =>
                  setAnswer(mainQuestion.key_to_update, answerOption.value)
                }
              >
                {answerOption.ui_name}
              </StyledSurveyButton>
            </StyledOption>
          ))}
        </StyledSurveyOptionsWrapper>
        {hasSubQuestions && answers[mainQuestion.key_to_update] === true && (
          <>
            {subQuestions.map((subQuestion) =>
              subQuestion?.answer_input_field_type === 'boolean' ? (
                <StyledRow key={subQuestion.id}>
                  <StyledColumn>
                    <StyledRow>
                      <BodyText>{subQuestion?.text}</BodyText>
                    </StyledRow>
                    <StyledSurveyOptionsWrapper>
                      {JSON.parse(
                        subQuestion?.answer_data
                      )?.answer_choices?.map((answerOption) => (
                        <StyledOption
                          key={`${subQuestion.id}${answerOption.ui_name}`}
                        >
                          <StyledSurveyButton
                            active={
                              answerOption.value ===
                              answers[subQuestion.key_to_update]
                            }
                            onClick={() =>
                              setAnswer(
                                subQuestion.key_to_update,
                                answerOption.value
                              )
                            }
                          >
                            {answerOption.ui_name}
                          </StyledSurveyButton>
                        </StyledOption>
                      ))}
                    </StyledSurveyOptionsWrapper>
                  </StyledColumn>
                </StyledRow>
              ) : (
                <StyledRow>
                  <StyledColumn>
                    <StyledRow>
                      <BodyText>{subQuestion?.text}</BodyText>
                    </StyledRow>
                    <StyledSurveyOptionsWrapper>
                      <Input
                        type="text"
                        value={answers[subQuestion.key_to_update]}
                        onChange={(e) =>
                          setAnswer(subQuestion.key_to_update, e.target.value)
                        }
                      />
                    </StyledSurveyOptionsWrapper>
                  </StyledColumn>
                </StyledRow>
              )
            )}
          </>
        )}
      </StyledColumn>
    </StyledRow>
  );
};

SurveyQuestion.propTypes = {
  data: PropTypes.object,
  answers: PropTypes.array,
  setAnswers: PropTypes.func,
  programMap: PropTypes.object,
  setProgramMap: PropTypes.func,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
};

const SurveyQuestions = ({
  answers,
  setAnswers,
  programMap,
  setProgramMap,
  loading,
  setLoading,
  graph_data,
  graph_loading,
  handle_graph_loading,
  configuration,
}) => {
  if (graph_loading) return handle_graph_loading();
  const questionsData = resolve(graph_data, 'roi_question_groups', []);
  if (questionsData.length === 0) return null;

  useEffect(() => {
    const programMapData = {};
    const colorPalette = resolve(
      configuration,
      'environment/journey/tools/graph_color_palette',
      StyledRoiChart.DefaultColorPalette
    );
    const answersData = questionsData.reduce((prev, current) => {
      current.roi_questions.forEach((roiQuestion) => {
        if (roiQuestion?.current_user_answers?.length) {
          if (roiQuestion.key_to_update === 'program_ids') {
            prev[
              roiQuestion.key_to_update
            ] = roiQuestion.current_user_answers.map((userAnswer) => {
              const numberUserAnswer = Number(userAnswer.body);
              const unUsedColors = colorPalette.filter(
                (c) => !Object.values(programMapData).includes(c)
              );
              programMapData[numberUserAnswer] =
                unUsedColors[Random(0, unUsedColors.length - 1)];
              return numberUserAnswer;
            });
          } else {
            if (roiQuestion.current_user_answers[0].body === 't') {
              prev[roiQuestion.key_to_update] = true;
            } else if (roiQuestion.current_user_answers[0].body === 'f') {
              prev[roiQuestion.key_to_update] = false;
            } else {
              prev[roiQuestion.key_to_update] =
                roiQuestion.current_user_answers[0].body;
            }
          }
        }
      });
      return prev;
    }, {});

    setAnswers(answersData);
    setProgramMap(programMapData);
  }, []);

  const questions = isEmpty(programMap)
    ? questionsData.slice(0, 2)
    : questionsData;
  return (
    <>
      {questions?.map((ROI_QUEST_GROUPS) => (
        <SurveyQuestion
          key={ROI_QUEST_GROUPS.ui_order}
          data={ROI_QUEST_GROUPS}
          answers={answers}
          setAnswers={setAnswers}
          programMap={programMap}
          setProgramMap={setProgramMap}
          loading={loading}
          setLoading={setLoading}
        />
      ))}
    </>
  );
};

SurveyQuestions.query = async () => await RoiQuestions();

SurveyQuestions.propTypes = {
  answers: PropTypes.object,
  setAnswers: PropTypes.func,
  programMap: PropTypes.object,
  setProgramMap: PropTypes.func,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  graph_data: PropTypes.object,
  graph_loading: PropTypes.bool,
  handle_graph_loading: PropTypes.func,
  configuration: PropTypes.func,
};

export default withData(SurveyQuestions);
